// Manual order, as the kit's Store does it (Store.swift: addTodo, addTodos, order(after:), order(before:), move).
// Every to-do has a fractional `order`; one put between two others takes the midpoint of its neighbours, so no other
// record changes. The neighbours are found among all the orders given (the Store looks at every to-do, not only the
// list's), so the same order never means two places.

/** On top of everything: one less than the smallest order, or -1 when there is none. */
export function orderAtTop(orders: Iterable<number>): number {
  return smallest(orders, 0) - 1;
}

/** At the end of everything: one more than the largest order, or 1 when there is none. */
export function orderAtEnd(orders: Iterable<number>): number {
  return largest(orders, 0) + 1;
}

/** Right after `order`: halfway to the next larger order, or one more when nothing comes after it. */
export function orderAfter(order: number, orders: Iterable<number>): number {
  let next: number | undefined;
  for (const other of orders) if (other > order && (next === undefined || other < next)) next = other;
  return next === undefined ? order + 1 : (order + next) / 2;
}

/** Right before `order`: halfway to the next smaller order, or one less when nothing comes before it. */
export function orderBefore(order: number, orders: Iterable<number>): number {
  let previous: number | undefined;
  for (const other of orders) if (other < order && (previous === undefined || other > previous)) previous = other;
  return previous === undefined ? order - 1 : (order + previous) / 2;
}

/**
 * Orders for `count` records added on top together, in the order given (Store.addTodos: the assistant's to-dos, a
 * pasted list): the first gets the smallest minus the count, each next one 1 more.
 */
export function ordersAtTop(count: number, orders: Iterable<number>): number[] {
  const first = smallest(orders, 0) - count;
  return Array.from({ length: count }, (_, index) => first + index);
}

function smallest(orders: Iterable<number>, fallback: number): number {
  let result: number | undefined;
  for (const order of orders) if (result === undefined || order < result) result = order;
  return result ?? fallback;
}

function largest(orders: Iterable<number>, fallback: number): number {
  let result: number | undefined;
  for (const order of orders) if (result === undefined || order > result) result = order;
  return result ?? fallback;
}
